/**
 * living_events — the ONE catalog of settlement events the world STAGES.
 *
 * This file is the editing surface for "what does a feast / rite / election /
 * battle look and feel like": venue, real-time duration, the crowd state the
 * attendees pose in, the props the client dresses the venue with, and the
 * label/glyph the HUD shows. The server's `lib/living_events/catalog.ts` is a
 * byte-identical mirror (the module bundle cannot reach across the monorepo) —
 * keep the two in sync; the server value is authoritative.
 *
 * Families → who renders them (client/src/modules/living-events/):
 *   festival → FestivalStage    (lanterns, banners, bonfire, fireworks, bunting)
 *   civic    → CivicStage       (podium, banners; listeners face the speaker)
 *   faith    → FaithStage       (shrine glow, candles, incense; worshippers bow)
 *   street   → StreetScene      (a speech bubble over the two talkers)
 *   battle   → BattleModule     (forces, generals, weapons, bolts, health bars)
 */

/** A street scene's detail may carry a two-line EXCHANGE — a line and its
 *  reply joined by this separator; the client shows the first over the host,
 *  the reply over the participant (eavesdrop bubbles, 2026-09-10). */
export const EXCHANGE_SEP = ' ‖ ';

export type EventFamily = 'festival' | 'civic' | 'faith' | 'street' | 'battle';
export type VenueSlot = 'plaza' | 'stage' | 'shrine' | 'tavern' | 'guardpost' | 'market' | 'here';
export type CrowdState = 'celebrating' | 'feasting' | 'praying' | 'listening' | 'attending' | 'chatting' | 'fighting';
export type PropKind =
  | 'lanterns' | 'banners' | 'bunting' | 'bonfire' | 'fireworks' | 'tables'
  | 'podium' | 'torches' | 'shrine_glow' | 'candles' | 'incense' | 'speech_bubble' | 'war_banner' | 'smoke';

export interface EventKindDef {
  kind: string;
  family: EventFamily;
  label: string;
  glyph: string;
  venue: VenueSlot;
  /** Real-time seconds the stage stays dressed and the crowd stays (festivals are LONG on purpose). */
  durationS: number;
  /** versona_state / versona_goal state the attendees pose in while the event is live. */
  crowdState: CrowdState;
  /** How far from the venue centre the crowd scatters (m). */
  crowdRadiusM: number;
  minParticipants: number;
  maxParticipants: number;
  props: PropKind[];
  /** Optional ambience clip key (client/public/audio/<key>.mp3) looped at the venue while live. */
  ambienceKey?: string;
}

export const EVENT_KINDS: Record<string, EventKindDef> = {
  feast:    { kind: 'feast',    family: 'festival', label: 'Feast',            glyph: '🍲', venue: 'plaza',     durationS: 900,  crowdState: 'feasting',    crowdRadiusM: 9,  minParticipants: 4, maxParticipants: 16, props: ['tables', 'lanterns', 'bunting', 'bonfire'] },
  festival: { kind: 'festival', family: 'festival', label: 'Festival',         glyph: '🎆', venue: 'plaza',     durationS: 1500, crowdState: 'celebrating', crowdRadiusM: 12, minParticipants: 6, maxParticipants: 24, props: ['lanterns', 'banners', 'bunting', 'bonfire', 'fireworks'] },
  rite:     { kind: 'rite',     family: 'faith',    label: 'Rite',             glyph: '◈',  venue: 'shrine',    durationS: 480,  crowdState: 'praying',     crowdRadiusM: 6,  minParticipants: 3, maxParticipants: 10, props: ['shrine_glow', 'candles', 'incense'] },
  faith:    { kind: 'faith',    family: 'faith',    label: 'Founding rite',    glyph: '✶',  venue: 'shrine',    durationS: 600,  crowdState: 'praying',     crowdRadiusM: 7,  minParticipants: 3, maxParticipants: 12, props: ['shrine_glow', 'candles', 'incense', 'banners'] },
  office:   { kind: 'office',   family: 'civic',    label: 'Investiture',      glyph: '⚜',  venue: 'plaza',     durationS: 300,  crowdState: 'attending',   crowdRadiusM: 7,  minParticipants: 3, maxParticipants: 10, props: ['podium', 'banners'] },
  election: { kind: 'election', family: 'civic',    label: 'Election hustings', glyph: '🗳', venue: 'plaza',     durationS: 600,  crowdState: 'listening',   crowdRadiusM: 9,  minParticipants: 4, maxParticipants: 18, props: ['podium', 'banners', 'bunting'] },
  speech:   { kind: 'speech',   family: 'civic',    label: 'Speech',           glyph: '📣', venue: 'stage',     durationS: 240,  crowdState: 'listening',   crowdRadiusM: 7,  minParticipants: 2, maxParticipants: 12, props: ['podium', 'torches'] },
  scene:    { kind: 'scene',    family: 'street',   label: 'Street scene',     glyph: '💬', venue: 'here',      durationS: 150,  crowdState: 'chatting',    crowdRadiusM: 1.5, minParticipants: 2, maxParticipants: 2, props: ['speech_bubble'] },
  sendoff:  { kind: 'sendoff',  family: 'civic',    label: 'March send-off',   glyph: '🎺', venue: 'plaza',     durationS: 300,  crowdState: 'listening',   crowdRadiusM: 9,  minParticipants: 3, maxParticipants: 18, props: ['podium', 'banners', 'torches'] },
  ballot:   { kind: 'ballot',   family: 'civic',    label: 'Voting day',       glyph: '🗳', venue: 'plaza',     durationS: 1200, crowdState: 'attending',   crowdRadiusM: 8,  minParticipants: 4, maxParticipants: 18, props: ['podium', 'banners', 'bunting'], ambienceKey: 'sfx_crowd_murmur' },
  declaration: { kind: 'declaration', family: 'civic', label: 'Declaration of war', glyph: '⚔', venue: 'plaza', durationS: 300, crowdState: 'listening', crowdRadiusM: 9, minParticipants: 3, maxParticipants: 18, props: ['podium', 'war_banner', 'torches'], ambienceKey: 'sfx_war_drums' },
  trial:    { kind: 'trial',    family: 'civic',    label: 'Trial',            glyph: '⚖',  venue: 'plaza',     durationS: 600,  crowdState: 'listening',   crowdRadiusM: 8,  minParticipants: 3, maxParticipants: 14, props: ['podium', 'torches'] },
  treaty:   { kind: 'treaty',   family: 'civic',    label: 'Treaty signing',   glyph: '🕊', venue: 'plaza',     durationS: 480,  crowdState: 'attending',   crowdRadiusM: 8,  minParticipants: 3, maxParticipants: 14, props: ['podium', 'banners'] },
  congress: { kind: 'congress', family: 'civic',    label: 'Peace congress',   glyph: '🏛', venue: 'plaza',     durationS: 900,  crowdState: 'listening',   crowdRadiusM: 9,  minParticipants: 4, maxParticipants: 18, props: ['podium', 'banners', 'torches'], ambienceKey: 'sfx_crowd_murmur' },
  rally:    { kind: 'rally',    family: 'civic',    label: 'Party rally',      glyph: '🚩', venue: 'stage',     durationS: 420,  crowdState: 'listening',   crowdRadiusM: 8,  minParticipants: 3, maxParticipants: 16, props: ['podium', 'banners'] },
  parade:   { kind: 'parade',   family: 'festival', label: 'Victory parade',   glyph: '🎺', venue: 'plaza',     durationS: 600,  crowdState: 'celebrating', crowdRadiusM: 12, minParticipants: 4, maxParticipants: 24, props: ['banners', 'bunting', 'torches'], ambienceKey: 'sfx_crowd_cheer' },
  legend:   { kind: 'legend',   family: 'festival', label: 'Legend night',     glyph: '📜', venue: 'tavern',    durationS: 900,  crowdState: 'listening',   crowdRadiusM: 5,  minParticipants: 3, maxParticipants: 14, props: ['bonfire', 'lanterns'], ambienceKey: 'sfx_crowd_murmur' },
  battle:   { kind: 'battle',   family: 'battle',   label: 'Battle',           glyph: '⚔',  venue: 'guardpost', durationS: 0,    crowdState: 'fighting',    crowdRadiusM: 14, minParticipants: 2, maxParticipants: 20, props: ['war_banner', 'smoke'] },
};

export const EVENT_KIND_LIST: readonly string[] = Object.keys(EVENT_KINDS);

/** Live-event rows older than this past their end are swept (kept briefly so a late client still sees "ended"). */
export const LIVING_EVENT_RETENTION_S = 600;

// ─── Battles: the stats every plan reads ────────────────────────────────────

export type BattlePhase = 'march' | 'engaged' | 'resolved';
export type BattlePlan = 'assault' | 'hold' | 'flank' | 'pincer' | 'skirmish' | 'retreat' | 'feint' | 'ambush' | 'envelop' | 'depth' | 'overwatch' | 'relief';
export type UnitRole = 'general' | 'captain' | 'attacker' | 'defender' | 'skirmisher' | 'medic' | 'mechanic';
export type UnitState = 'marching' | 'fighting' | 'healing' | 'withdrawing' | 'retreating' | 'recovering' | 'hiding' | 'repairing' | 'routed' | 'fallen' | 'done';
/** battle_event kinds (war rework 2026-09-16): one row per ACTION, never per bolt. */
export type BattleEventKind = 'joined' | 'order' | 'shot' | 'blocked' | 'heal' | 'suppressed' | 'routed' | 'withdraw' | 'incapacitated' | 'fallen' | 'objective' | 'resolved' | 'reload' | 'turret' | 'reinforce' | 'lesson' | 'advice' | 'retreat' | 'hide' | 'rally' | 'strafe' | 'missile' | 'dogfight';
export type ObjectiveState = 'controlled' | 'contested' | 'securing' | 'uncontrolled';

export const BATTLE_PLANS: readonly BattlePlan[] = ['assault', 'hold', 'flank', 'pincer', 'skirmish', 'retreat', 'feint', 'ambush', 'envelop', 'depth', 'overwatch', 'relief'];
export const PLAN_LABEL: Record<BattlePlan, string> = {
  assault: 'all-out assault', hold: 'hold the line', flank: 'flank the wing', pincer: 'pincer — centre and both wings',
  skirmish: 'skirmish and fall back', retreat: 'full retreat',
  // war campaign wave 2026-09-16 — the strategies the captains and the general reason with
  feint: 'feint — draw them onto our guns', ambush: 'ambush — lie hidden until they are close', envelop: 'envelop — wide wings, close the ring',
  depth: 'defence in depth — two lines, yield ground for time', overwatch: 'bounding overwatch — half covers, half moves', relief: 'hold for the relief — the reinforcements decide it',
};
/** Which squad plans a general's INTENT sways toward (compatible plans get COMPAT_SWAY on top of the intent's own GENERAL_SWAY). */
export const STRATEGY_COMPAT: Record<BattlePlan, readonly BattlePlan[]> = {
  assault: ['overwatch', 'flank', 'envelop'], hold: ['depth', 'ambush', 'overwatch'], flank: ['envelop', 'feint', 'assault'], pincer: ['envelop', 'flank'],
  skirmish: ['feint', 'overwatch'], retreat: ['skirmish', 'depth'], feint: ['ambush', 'skirmish'], ambush: ['hold', 'depth'], envelop: ['pincer', 'flank'],
  depth: ['hold', 'ambush'], overwatch: ['assault', 'hold'], relief: ['hold', 'depth', 'overwatch'],
};

/** Tunables (server authoritative; the client reads them for display only). */
export const BATTLE = {
  FORCE_MIN: 6,   // war tweaks 2026-09-17: bigger lines fight longer (was 4)
  FORCE_MAX: 24,  // (was 18)
  /** Extra fighters a rich treasury can field: +1 per this many coins. */
  FORCE_PER_TREASURY: 100,
  /** Siege (march on the town) when the aggressor's Lanchester strength beats the defender's by this ratio. */
  SIEGE_RATIO: 1.3,
  MARCH_SPEED_MPS: 2,
  /** A march that has not met in this long engages where it stands (real seconds). */
  MARCH_MAX_S: 2400,
  ENGAGE_RADIUS_M: 30,
  ROUND_MAX: 600,
  ROUT_MORALE: 30,
  ROUT_CASCADE_M: 12,
  GENERAL_AURA_M: 20,
  MEDIC_HEAL: 12,
  MEDIC_RANGE_M: 6,
  HIT_BASE: 0.34,   // war campaign wave: was 0.55 — a coin flip made an 8-v-8 a six-round affair
  FLANK_DAMAGE_MULT: 1.3,
  /** Exhaustion a settlement takes per battle, before losses. */
  EXHAUSTION_BASE: 10,
  EXHAUSTION_PER_LOSS: 5,
  /** Deaths a single battle may inflict; a lethal hit past the cap routs the victim at 1 hp instead. */
  CASUALTY_CAP_PER_BATTLE: 2,
  /** Real seconds between rounds once engaged (the tick is faster than a fight should read). */
  ROUND_MIN_S: 3,
  /** A battle goal outlives the tick cadence (war tweaks 2026-09-18): rounds re-goal every tick, but the sim tick
   *  lands every ~30 s under load and an EXPIRED goal hands the versona back to its schedule — the wounded walked
   *  18 s toward the medics and 12 s back home, every round. */
  GOAL_TTL_S: 120,
  /** Bodies lie, smoke drifts: how long the battle stage lingers after the last round. */
  AFTERMATH_S: 180,
  /** A general re-plans every this many rounds (and at first contact). */
  REPLAN_EVERY: 10,
  /** Real seconds between battles: base × politics/resources multipliers, clamped. */
  INTERVAL_BASE_S: 1500,
  INTERVAL_MAX_S: 21600,
  // ── war rework 2026-09-16 ──
  /** Suppression pressure a shot AT you adds (a hit vs a miss), decaying with this half-life. */
  SUPPRESS_HIT: 0.18,
  SUPPRESS_MISS: 0.10,
  SUPPRESS_HALF_LIFE_S: 6,
  SUPPRESSED_AT: 0.6,
  PINNED_AT: 0.85,
  /** In cover a shot presses half as hard and the defender gains this defense. */
  COVER_SUPPRESS_MULT: 0.5,
  COVER_DEFENSE: 10,
  COVER_SEARCH_M: 8,
  SUPPRESSED_HIT_PENALTY: 0.15,
  /** A retreat WITHDRAWS: rally this far toward home; the enemy pursues for PURSUIT_ROUNDS. */
  WITHDRAW_RALLY_M: 60,
  WITHDRAW_ROUNDS_MAX: 10,
  PURSUIT_ROUNDS: 2,
  /** The objective every battle is fought for. */
  OBJECTIVE_RADIUS_M: 14,
  OBJECTIVE_SECURE_S: 300, // war tweaks 2026-09-17: a gate takes five uncontested minutes to fall (was 90)
  /** battle_event rows older than this are pruned each round. */
  EVENT_KEEP_S: 90,
  /** Morale fatigue starts after this many ENGAGED seconds (was round 40). */
  FATIGUE_ONSET_S: 900, // war tweaks 2026-09-17: no fatigue before the battle's minimum (was 480)
  /** Fit-for-duty after a light / severe wound (real seconds). */
  INJURY_LIGHT_S: 3600,
  INJURY_SEVERE_S: 21600,
  /** A player's bolt must be this close to the CHOREOGRAPHED hull. */
  AIR_SHOOT_RANGE_M: 700,
  /** The map remembers a battle (battle_record) this long. */
  RECORD_KEEP_S: 604800,
  // ── war campaign wave 2026-09-16: longer rounds, reinforcements, outposts, squads, advice, lessons ──
  /** Hit chance falls with distance: × (1 − FALLOFF × d/range). */
  HIT_RANGE_FALLOFF: 0.35,
  /** Every battle blow is scaled by this (100 hp must survive more than three hits). */
  DAMAGE_SCALE: 0.6,
  /** A moving unit advances at most this far in a round (the 3–5 s rush) and does not fire that round. */
  RUSH_M: 12,
  /** Ranged units stop at this share of their range. */
  ENGAGE_STOP: 0.85,
  MORALE_HIT: 2,
  MORALE_CASUALTY: 15,
  ROUT_BASE: 0.45,
  /** Reinforcements: a call opens on a death or under this share of the initial force; drafted from same-civ towns within this radius. */
  CALL_COOLDOWN_ROUNDS: 15,
  CALL_STRENGTH: 0.7,
  REINFORCE_PER_CALL: 6,
  REINFORCE_RADIUS_M: 30000,
  /** Outposts: one per side, this far from the target toward home; the turrets' reach and bite. */
  OUTPOST_OFF_M: 110,
  OUTPOST_RADIUS_M: 14,
  OUTPOST_TURRETS: 2,
  TURRET_RANGE_M: 45,
  TURRET_HIT: 0.45,
  TURRET_DMG: 14,
  /** The wounded: retreat to the medics under RETREAT_HP, back to the line at RETURN_HP; no medics → hide. */
  RETREAT_HP: 35,
  RETURN_HP: 70,
  OUTPOST_HEAL_RANGE_M: 10,
  NO_MEDIC_MORALE: 3,
  NO_MEDIC_ROUT_BONUS: 0.15,
  HIDE_SPOT_M: 12,
  HIDE_ROUNDS_MAX: 10,
  /** Mechanics: a hull under REPAIR_AT lands for REPAIR_HP × mechanics per exchange epoch, up at REPAIRED_AT. */
  REPAIR_AT: 0.4,
  REPAIRED_AT: 0.8,
  REPAIR_HP: 12,
  MECHANICS_MAX: 2,
  /** Squads under captains; the general's intent sways them. */
  /** each side's line forms this far on ITS OWN side of the target and closes under overwatch (the lines used to meet 4 m apart — a scrum) */
  STANDOFF_M: 9,
  SQUAD_SIZE: 6,
  GENERAL_SWAY: 0.35,
  COMPAT_SWAY: 0.15,
  /** Lessons (memories) and advice (the LLM in the background) bias the utilities, never command. */
  LESSON_BIAS: 0.2,
  LESSON_CLAMP: 0.4,
  LESSON_SHARE_N: 6,
  ADVICE_EVERY_ROUNDS: 12,
  ADVICE_TTL_ROUNDS: 30,
  ADVICE_BIAS: 0.5,
  // ── war tweaks 2026-09-17: length, rallies, decay, the sky ──
  /** A battle lasts at least this long once engaged (real seconds): no retreat plan, no withdrawal completes, no gate falls, an emptied line rallies. */
  BATTLE_MIN_S: 900,
  /** …and at most this long: the fight wears itself out on strength (rounds are ticks — 4–30 s — so time is the clock). */
  BATTLE_MAX_S: 1800,
  /** Under the minimum the routed (hp ≥ RALLY_HP, never the spared) rally every this many rounds. */
  RALLY_EVERY_ROUNDS: 12,
  RALLY_HP: 20,
  /** An outpost whose battle is over is deleted this long after resolution re-stamped it; the client decays it on the same clock. */
  OUTPOST_DECAY_S: 1800,
  /** The war layer draws a fought battle for this long after its resolution. */
  RECORD_MAP_S: 900,
  /** Fighters strafe: a dive that completed since the last tick fires on the nearest enemy unit within reach of its aim. */
  AIR_STRAFE_REACH_M: 100,
  AIR_STRAFE_HIT: 0.35,
  AIR_STRAFE_DMG: 6,
  AIR_STRAFE_DMG_SPREAD: 4,
  /** Cruisers: one missile per exchange epoch — at the enemy cruiser (air) or a ground unit (+ splash within the radius). */
  AIR_MISSILE_AIR_SHARE: 0.3,
  AIR_MISSILE_AIR_DMG: 24,
  AIR_MISSILE_DMG: 14,
  AIR_MISSILE_SPLASH: 6,
  AIR_MISSILE_SPLASH_M: 8,
  /** THE DOGFIGHT IS REAL (war tweaks 2026-09-18): each cross-side pair's CHASER fires a burst at its evader every
   *  AIR_BURST_S (one catch-up per tick), a hit for AIR_BURST_DMG ± the spread; a `dogfight` battle_event per burst.
   *  The 90 s epoch still swaps who chases; it no longer rolls invisible damage. */
  AIR_BURST_S: 20,
  AIR_BURST_HIT: 0.5,
  AIR_BURST_DMG: 5,
  AIR_BURST_DMG_SPREAD: 3,
  /** A hull that departed after its battle is deleted this long after (2–4 km up and away by then). */
  AIR_DEPART_TTL_S: 240,
  /** The economy-sized wing's ceilings. */
  AIR_FIGHTERS_MAX: 7,
  AIR_CRUISERS_MAX: 3,
} as const;
